import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { get, hostError, post, type NodeInfo } from '../../api'
import { t, type Key } from '../../i18n'
import { Button, Note } from '../../ui'
import { GroupDialog } from './GroupDialog'
import { clock } from './format'
import { one, SwitchToast, tn } from './nodeSwitch'
import { Empty, ErrorBox, NetPanel, SkeletonRows } from './ui'

/*
 * Address bans of a node (BAN-UI, owner's decision 2 of 02.11): the client
 * bans in force on the node's ingest, read from the node through the center
 * (GET /api/admin/nodes/{id}/bans), and lifting one or all of them
 * (POST …/bans/unban) with the operator's code — ConfirmDialog level 3 with a
 * plain button: lifting a ban breaks nothing on air. A ban lives on the
 * node, never on a node group, so a group member's card has the same tab.
 *
 * Design system (1793734629-c6d4): .sx-table.compact (MetricsTable /
 * ConsoleNodeTable: addresses and numbers mono, row action one plain small
 * button), EmptyStates, .sx-note for a node that can't say, ConfirmDialog
 * through GroupDialog, a toast for the result. The words are not in
 * 09-voice yet: DS: BU-01…BU-12 (design/redesign/AUDIT.md §36).
 */

export interface NodeBanReason {
  reason: string
  count: number
}

export interface NodeBan {
  client: string
  until: string
  reasons: NodeBanReason[]
}

export type BansState = 'ok' | 'offline' | 'node_outdated' | 'holders_outdated' | 'ingress_unavailable' | 'no_ingest' | 'error'

export interface NodeBans {
  node: string
  state: BansState
  bans: NodeBan[]
  total: number
  lifted?: number
  as_of: string
}

export const bansKey = (id: string) => ['admin', 'node', id, 'bans'] as const

const STATES = new Set<BansState>(['offline', 'node_outdated', 'holders_outdated', 'ingress_unavailable', 'no_ingest', 'error'])

/** A refusal reason in words (DS: BU-09); an unknown one as the node said it. */
export function reasonWord(r: string): string {
  const base = r.startsWith('stream suspended by an administrator') ? 'stream suspended by an administrator' : r
  const k = `bans.reason.${base}` as Key
  const w = t(k)
  return w === k ? r : w
}

/** "in 3 min" / "in 40 s" until a time. */
function inTime(until: string, now: number): string {
  const s = Math.max(0, Math.round((Date.parse(until) - now) / 1000))
  return s >= 60 ? `${Math.ceil(s / 60)} min` : `${s} s`
}

type Ask = { kind: 'one'; client: string } | { kind: 'all'; n: number }

/** "14:05:09 in 4 min": the end of a ban, UTC with seconds (11 «Time»). */
function Until({ until, now }: { until: string; now: number }) {
  return (
    <>
      <span className="sx-mono">{clock(until)}</span> <span className="text-muted">{tn('bans.in', { t: inTime(until, now) })}</span>
    </>
  )
}

/** The refusals behind a ban, in words with their counts (DS: BU-09). */
function Reasons({ reasons }: { reasons: NodeBanReason[] }) {
  if (reasons.length === 0) return <span className="text-muted">—</span>
  return (
    <>
      {reasons.map((r, i) => (
        <span key={r.reason} className="whitespace-nowrap">
          {i > 0 && ', '}
          {reasonWord(r.reason)} <span className="sx-mono text-text-soft">×{r.count}</span>
        </span>
      ))}
    </>
  )
}

export function NodeBansPanel({ n }: { n: NodeInfo }) {
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: bansKey(n.id),
    queryFn: () => get<NodeBans>(`/api/admin/nodes/${encodeURIComponent(n.id)}/bans`),
    refetchInterval: 10_000,
  })
  const [ask, setAsk] = useState<Ask | null>(null)
  const [toast, setToast] = useState<{ state: 'live' | 'idle'; title: ReturnType<typeof tn> } | null>(null)
  const trigger = useRef<HTMLElement | null>(null)
  const open = (a: Ask, el: HTMLElement) => {
    trigger.current = el
    setAsk(a)
  }
  const d = q.data
  const now = d ? Date.parse(d.as_of) : Date.now()
  const live = d?.state === 'ok' ? d.bans.filter((b) => Date.parse(b.until) > now) : []

  let body
  if (q.isPending) {
    body = <SkeletonRows rows={3} cols={4} />
  } else if (q.error && !d) {
    body = <ErrorBox testId="node-bans-error" text={`GET /api/admin/nodes/${n.id}/bans: ${(q.error as Error).message}`} busy={q.isFetching} onRetry={() => void q.refetch()} />
  } else if (d && STATES.has(d.state)) {
    body = (
      <Note state={d.state === 'node_outdated' || d.state === 'holders_outdated' || d.state === 'no_ingest' ? undefined : 'warn'} data-testid="node-bans-state">
        <span data-state-code={d.state}>{t(`bans.state.${d.state}` as Key)}</span>
      </Note>
    )
  } else if (live.length === 0) {
    body = (
      <Empty title={t('bans.empty.title')} testId="node-bans-empty" icon="check">
        {t('bans.empty.body')}
      </Empty>
    )
  } else {
    body = (
      <div className="flex flex-col gap-2">
        <div className="hidden overflow-x-auto md:block">
          <table className="sx-table compact w-full" data-testid="node-bans-table">
            <thead>
              <tr>
                <th scope="col">{t('bans.col.address')}</th>
                <th scope="col">{t('bans.col.until')}</th>
                <th scope="col">{t('bans.col.reasons')}</th>
                <th scope="col" className="num">
                  <span className="sr-only">{t('group.col.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {live.map((b) => (
                <tr key={b.client} data-testid={`ban-row-${b.client}`}>
                  <td className="sx-mono">{b.client}</td>
                  <td className="tabular whitespace-nowrap">
                    <Until until={b.until} now={now} />
                  </td>
                  <td className="!whitespace-normal">
                    <Reasons reasons={b.reasons} />
                  </td>
                  <td className="num">
                    <Button size="sm" className="max-md:min-h-[44px]" data-testid={`ban-lift-${b.client}`} onClick={(e) => open({ kind: 'one', client: b.client }, e.currentTarget)}>
                      {t('bans.btn.lift')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* phone (LayoutBreakpoints, up to 767 px): one card per address, the action 44 px */}
        <ul className="flex flex-col md:hidden" data-testid="node-bans-cards">
          {live.map((b) => (
            <li key={b.client} className="flex flex-col gap-1.5 border-t border-line py-3 first:border-t-0 first:pt-0" data-testid={`ban-card-${b.client}`}>
              <span className="sx-mono break-all text-text">{b.client}</span>
              <span className="text-[13px] text-text-soft">
                <span className="sx-label mr-2">{t('bans.col.until')}</span>
                <Until until={b.until} now={now} />
              </span>
              <span className="text-[13px] text-text-soft">
                <span className="sx-label mr-2">{t('bans.col.reasons')}</span>
                <Reasons reasons={b.reasons} />
              </span>
              <Button size="sm" className="min-h-[44px] self-start" data-testid={`ban-lift-card-${b.client}`} onClick={(e) => open({ kind: 'one', client: b.client }, e.currentTarget)}>
                {t('bans.btn.lift')}
              </Button>
            </li>
          ))}
        </ul>
        {d!.total > live.length && (
          <p className="sx-cap" data-testid="node-bans-more">
            {tn('bans.more', { n: live.length, total: d!.total })}
          </p>
        )}
      </div>
    )
  }

  const done = async (r: NodeBans, a: Ask) => {
    setAsk(null)
    qc.setQueryData(bansKey(n.id), r)
    await qc.invalidateQueries({ queryKey: bansKey(n.id) })
    const lifted = r.lifted ?? 0
    if (lifted === 0) setToast({ state: 'idle', title: tn('bans.toast.none', { name: n.name }) })
    else if (a.kind === 'one') setToast({ state: 'live', title: tn('bans.toast.one', { name: n.name, client: a.client }) })
    else setToast({ state: 'live', title: tn(one(lifted, 'bans.toast.all.one', 'bans.toast.all.many'), { name: n.name, n: lifted }) })
  }

  return (
    <NetPanel
      title={
        <h2 className="text-ui-title font-medium">
          {t('bans.tab')}
          {d?.state === 'ok' && <span className="tabular ml-2 font-mono text-xs text-muted" data-testid="node-bans-count">{d.total}</span>}
        </h2>
      }
      aside={
        live.length > 1 ? (
          <Button size="sm" className="max-md:min-h-[44px]" data-testid="ban-lift-all" onClick={(e) => open({ kind: 'all', n: d!.total }, e.currentTarget)}>
            {t('bans.btn.liftAll')}
          </Button>
        ) : undefined
      }
      testId="node-bans"
    >
      <div className="flex flex-col gap-3">
        <p className="max-w-[80ch] text-[13px] text-text-soft">{t('bans.intro')}</p>
        {body}
      </div>
      {ask && (
        <GroupDialog
          testId="ban-lift"
          title={ask.kind === 'one' ? tn('bans.confirm.one', { client: ask.client, name: n.name }) : tn('bans.confirm.all', { name: n.name })}
          facts={[ask.kind === 'one' ? tn('bans.fact.one', { client: ask.client }) : tn('bans.fact.all', { n: ask.n }), t('bans.fact.again')]}
          action={ask.kind === 'one' ? t('bans.btn.lift') : t('bans.btn.liftAll')}
          busyLabel={t('bans.busy')}
          returnFocus={trigger}
          onClose={() => setAsk(null)}
          submit={async (totp) => {
            const body = ask.kind === 'one' ? { client: ask.client, totp } : { all: true, totp }
            const r = await post<NodeBans>(`/api/admin/nodes/${encodeURIComponent(n.id)}/bans/unban`, body)
            await done(r, ask)
          }}
          errorText={(e) => {
            const h = hostError(e)
            if (h?.code && STATES.has(h.code as BansState)) return t(`bans.state.${h.code}` as Key)
            return t('bans.error', { why: h ? `${h.status} ${h.error}` : (e as Error).message })
          }}
        />
      )}
      {toast && <SwitchToast o={toast} testId="ban-toast" onClose={() => setToast(null)} />}
    </NetPanel>
  )
}
